const STORAGE_KEY = 'brix.purchasesTable';

export type SortDir = 'asc' | 'desc';

export type PurchasesSortField =
  | 'number'
  | 'txn_date'
  | 'vendor_name'
  | 'total_amount'
  | 'balance'
  | 'status'
  | 'received_into_inventory';

export const PURCHASES_SORT_FIELDS: readonly PurchasesSortField[] = [
  'number',
  'txn_date',
  'vendor_name',
  'total_amount',
  'balance',
  'status',
  'received_into_inventory',
] as const;

const PURCHASES_SORT_FIELD_SET = new Set<string>(PURCHASES_SORT_FIELDS);

export type PurchasesTablePreferences = {
  sort: PurchasesSortField | `-${PurchasesSortField}`;
  perPage: number;
};

const DEFAULT_PREFERENCES: PurchasesTablePreferences = {
  sort: '-txn_date',
  perPage: 100,
};

export const encodePurchasesSort = (
  field: PurchasesSortField,
  direction: SortDir
): PurchasesTablePreferences['sort'] => (direction === 'desc' ? `-${field}` : field);

export const parsePurchasesSort = (
  value: string
): { field: PurchasesSortField; direction: SortDir } | null => {
  const direction: SortDir = value.startsWith('-') ? 'desc' : 'asc';
  const field = (value.startsWith('-') ? value.slice(1) : value) as PurchasesSortField;

  if (!PURCHASES_SORT_FIELD_SET.has(field)) {
    return null;
  }

  return { field, direction };
};

export const loadPurchasesTablePreferences = (): PurchasesTablePreferences => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return DEFAULT_PREFERENCES;
    }

    const parsed = JSON.parse(raw) as Partial<PurchasesTablePreferences>;
    const sort =
      typeof parsed.sort === 'string' && parsePurchasesSort(parsed.sort)
        ? (parsed.sort as PurchasesTablePreferences['sort'])
        : DEFAULT_PREFERENCES.sort;
    const perPage =
      typeof parsed.perPage === 'number' && parsed.perPage >= 1 && parsed.perPage <= 100
        ? parsed.perPage
        : DEFAULT_PREFERENCES.perPage;

    return { sort, perPage };
  } catch {
    return DEFAULT_PREFERENCES;
  }
};

export const savePurchasesTablePreferences = (prefs: PurchasesTablePreferences): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Ignore quota / private browsing errors.
  }
};
